import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import ProductCard from '../components/ProductCard';
import Seo from '../components/Seo';
import { trackEvent } from '../utils/tracking';
import { SITE_URL, absoluteUrl, categoryPath, categorySeoFor, productCategorySlug, textSummary } from '../utils/seo';
import { useCategories } from '../utils/categories';
import './ProductDetail.css';

// Some API rows return JSON array columns as raw strings — coerce to arrays.
const asArray = (v) => {
  if (Array.isArray(v)) return v;
  if (typeof v === 'string') {
    try { const p = JSON.parse(v); return Array.isArray(p) ? p : []; } catch { return []; }
  }
  return [];
};

const normalizeProduct = (p) => p && ({
  ...p,
  images: asArray(p.images),
  ingredients: asArray(p.ingredients),
  allergens: asArray(p.allergens),
  tags: asArray(p.tags),
});

const getDeliveryDate = (country) => {
  const today = new Date();
  const days = country === 'India' ? 5 : country === 'Singapore' || country === 'Malaysia' ? 10 : 14;
  const delivery = new Date(today.setDate(today.getDate() + days));
  return delivery.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' });
};

const ProductDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [activeImg, setActiveImg] = useState(0);
  const [activeTab, setActiveTab] = useState('description');
  const [related, setRelated] = useState([]);
  const [recentlyViewed, setRecentlyViewed] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('recentlyViewed') || '[]');
    } catch {
      return [];
    }
  });

  // Review form state
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const cats = useCategories();

  const { addItem, subtotal } = useCart();
  const { user } = useAuth();

  const fetchProduct = (productSlug) => {
    return api.get(`/products/${productSlug}`).then(r => {
      const p = normalizeProduct(r.data.product);
      setProduct(p);
      setSelectedVariant(p.variants?.[0] || null);
      setLoading(false);
      return p;
    }).catch(() => setLoading(false));
  };

  useEffect(() => {
    fetchProduct(slug);
  }, [slug]);

  // Update recently viewed when product loads
  useEffect(() => {
    if (!product) return;

    trackEvent('product_view', {
      productId: product._id,
      metadata: { name: product.name, category: product.category, source: 'product_detail' },
    });

    const stored = (() => {
      try {
        return JSON.parse(localStorage.getItem('recentlyViewed') || '[]');
      } catch {
        return [];
      }
    })();

    const filtered = stored.filter(p => p._id !== product._id);
    const entry = {
      _id: product._id,
      name: product.name,
      slug: product.slug,
      thumbnail: product.thumbnail,
      category: product.category,
      variants: product.variants,
    };
    const updated = [entry, ...filtered].slice(0, 4);
    localStorage.setItem('recentlyViewed', JSON.stringify(updated));
    setRecentlyViewed(updated.filter(p => p._id !== product._id));
  }, [product]);

  // Fetch related products
  useEffect(() => {
    if (!product) return;
    api.get(`/products/${product.slug}/related`)
      .then(r => setRelated(r.data.products || r.data || []))
      .catch(() => setRelated([]));
  }, [product]);

  if (loading) return (
    <>
      <Seo title="Loading Product | Avakaaya Foods" description="Loading product information from Avakaaya Foods." path={`/products/${slug}`} noIndex />
      <div className="container" style={{ padding: '80px 24px', textAlign: 'center' }}>Loading...</div>
    </>
  );
  if (!product) return (
    <>
      <Seo title="Product Not Found | Avakaaya Foods" description="This product is no longer available." path={`/products/${slug}`} noIndex />
      <div className="container" style={{ padding: '80px 24px', textAlign: 'center' }}>Product not found.</div>
    </>
  );

  const discount = selectedVariant ? Math.round((1 - selectedVariant.price / selectedVariant.mrp) * 100) : 0;
  const images = product.images?.length ? product.images : [product.thumbnail];

  const isOutOfStock = selectedVariant?.stock !== undefined && selectedVariant.stock <= 0;
  const canonicalPath = `/products/${product.slug}`;
  const productCollection = productCategorySlug(product);
  const collectionName = categorySeoFor(productCollection, cats)?.name || product.category;
  const productImages = images.filter(Boolean).map(absoluteUrl);
  const productSchema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: product.description,
    image: productImages,
    sku: product.variants?.[0]?.sku || String(product._id),
    category: collectionName,
    brand: { '@type': 'Brand', name: 'Avakaaya Foods' },
    ...(product.variants?.length ? {
      offers: product.variants.map(variant => ({
        '@type': 'Offer',
        url: `${SITE_URL}${canonicalPath}`,
        sku: variant.sku || undefined,
        priceCurrency: 'INR',
        price: String(variant.price),
        availability: variant.stock > 0 ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
        itemCondition: 'https://schema.org/NewCondition',
      })),
    } : {}),
    ...(product.rating > 0 && product.numReviews > 0 ? {
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: product.rating,
        reviewCount: product.numReviews,
      },
    } : {}),
  };
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'Products', item: `${SITE_URL}/products` },
      { '@type': 'ListItem', position: 3, name: collectionName, item: `${SITE_URL}${categoryPath(productCollection)}` },
      { '@type': 'ListItem', position: 4, name: product.name, item: `${SITE_URL}${canonicalPath}` },
    ],
  };

  const handleAddToCart = () => {
    if (isOutOfStock) return;
    addItem(product, selectedVariant, quantity);
    const addedValue = (Number(selectedVariant.price) || 0) * quantity;
    trackEvent('add_to_cart', {
      productId: product._id,
      cartValue: subtotal + addedValue,
      cartItems: [{ productId: product._id, name: product.name, weight: selectedVariant.weight, quantity, price: selectedVariant.price }],
      metadata: { source: 'product_detail', addedValue },
    });
    toast.success(`${product.name} (${selectedVariant.weight} × ${quantity}) added to cart!`);
  };

  const handleBuyNow = () => {
    if (isOutOfStock) return;
    addItem(product, selectedVariant, quantity);
    const addedValue = (Number(selectedVariant.price) || 0) * quantity;
    trackEvent('add_to_cart', {
      productId: product._id,
      cartValue: subtotal + addedValue,
      cartItems: [{ productId: product._id, name: product.name, weight: selectedVariant.weight, quantity, price: selectedVariant.price }],
      metadata: { source: 'product_detail_buy_now', addedValue },
    });
    trackEvent('begin_checkout', {
      cartValue: subtotal + addedValue,
      cartItems: [{ productId: product._id, name: product.name, weight: selectedVariant.weight, quantity, price: selectedVariant.price }],
      metadata: { source: 'product_detail_buy_now', subtotal: subtotal + addedValue, shippingStatus: 'pending_address' },
    });
    navigate('/checkout');
  };

  const hasUserReviewed = user && product.reviews?.some(r => r.user?.toString() === user._id?.toString());

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    if (!reviewRating) { toast.error('Please select a rating'); return; }
    setReviewSubmitting(true);
    try {
      await api.post(`/products/${product._id}/review`, { rating: reviewRating, comment: reviewComment });
      toast.success('Review submitted!');
      setReviewRating(0);
      setReviewComment('');
      await fetchProduct(slug);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to submit review');
    } finally {
      setReviewSubmitting(false);
    }
  };

  return (
    <div className="product-detail-page">
      <Seo
        title={`${product.name} Online | Avakaaya Foods`}
        description={textSummary(product.shortDescription || product.description)}
        path={canonicalPath}
        image={product.thumbnail || productImages[0]}
        type="product"
        jsonLd={[productSchema, breadcrumbSchema]}
      />
      <div className="container">
        {/* Breadcrumb */}
        <nav className="breadcrumb">
          <Link to="/">Home</Link>
          <span>›</span>
          <Link to="/products">Products</Link>
          <span>›</span>
          <Link to={categoryPath(productCollection)}>{collectionName}</Link>
          <span>›</span>
          <span>{product.name}</span>
        </nav>

        <div className="product-detail-grid">
          {/* Images */}
          <div className="product-images">
            <div className="product-img-main">
              <img
                src={images[activeImg] || product.thumbnail}
                alt={product.name}
                className="product-img"
              />
              {discount > 5 && <span className="detail-discount-badge">Save {discount}%</span>}
            </div>
            {images.length > 1 && (
              <div className="product-img-thumbs">
                {images.map((img, i) => (
                  <button
                    key={i}
                    className={`img-thumb ${activeImg === i ? 'active' : ''}`}
                    onClick={() => setActiveImg(i)}
                  >
                    <img src={img} alt={`${product.name} ${i + 1}`} />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div className="product-info">
            <div className="product-badges">
              <span className={`badge ${product.isVeg ? 'badge-veg' : 'badge-nonveg'}`}>
                {product.isVeg ? '● Vegetarian' : '● Non-Vegetarian'}
              </span>
              {product.tags?.includes('bestseller') && <span className="badge badge-bestseller">Bestseller</span>}
            </div>

            <h1 className="product-detail-name">{product.name}</h1>

            {/* Rating */}
            {product.rating > 0 && (
              <div className="product-rating">
                <span className="stars">{'★'.repeat(Math.round(product.rating))}{'☆'.repeat(5 - Math.round(product.rating))}</span>
                <span className="rating-count">{product.rating.toFixed(1)} ({product.numReviews} reviews)</span>
              </div>
            )}

            {/* Variant selector */}
            <div className="detail-section">
              <label className="detail-label">
                Weight: <strong>{selectedVariant?.weight}</strong>
              </label>
              <div className="variant-grid">
                {product.variants.map(v => {
                  const d = Math.round((1 - v.price / v.mrp) * 100);
                  return (
                    <button
                      key={v.weight}
                      className={`variant-card ${selectedVariant?.weight === v.weight ? 'active' : ''}`}
                      onClick={() => setSelectedVariant(v)}
                    >
                      <span className="vc-weight">{v.weight}</span>
                      <span className="vc-price">₹{v.price}</span>
                      {d > 5 && <span className="vc-discount">-{d}%</span>}
                    </button>
                  );
                })}
              </div>
              {/* Stock level indicator */}
              {selectedVariant?.stock !== undefined && selectedVariant.stock <= 0 && (
                <span className="stock-out">Out of stock</span>
              )}
              {selectedVariant?.stock !== undefined && selectedVariant.stock > 0 && selectedVariant.stock <= 10 && (
                <span className="stock-low">Only {selectedVariant.stock} left!</span>
              )}
            </div>

            {/* Price */}
            <div className="product-price-row">
              <span className="detail-price">₹{selectedVariant?.price}</span>
              {selectedVariant?.mrp > selectedVariant?.price && (
                <>
                  <span className="detail-mrp">₹{selectedVariant?.mrp}</span>
                  <span className="detail-save">You save ₹{selectedVariant.mrp - selectedVariant.price}</span>
                </>
              )}
            </div>

            {/* Quantity */}
            <div className="detail-section">
              <label className="detail-label">Quantity</label>
              <div className="quantity-row">
                <div className="quantity-control">
                  <button onClick={() => setQuantity(q => Math.max(1, q - 1))} className="qty-btn">−</button>
                  <span className="qty-val">{quantity}</span>
                  <button onClick={() => setQuantity(q => q + 1)} className="qty-btn">+</button>
                </div>
                <button
                  className="btn btn-outline btn-lg"
                  onClick={handleAddToCart}
                  disabled={isOutOfStock}
                >
                  {isOutOfStock ? 'Out of Stock' : 'Add to Cart'}
                </button>
              </div>
              {!isOutOfStock && (
                <button
                  className="btn btn-gold btn-lg buy-now-btn"
                  onClick={handleBuyNow}
                >
                  Buy Now
                </button>
              )}
            </div>

            {/* Trust signals */}
            <div className="product-trust">
              <div className="trust-row">🚚 <span>Delivery in 1–2 days within India</span></div>
              <div className="trust-row">🌍 <span>Ships to USA, UK, Singapore, Australia, Malaysia</span></div>
              <div className="trust-row">🌿 <span>No artificial preservatives or colors</span></div>
              <div className="trust-row">📦 <span>Shelf life: {product.shelfLife}</span></div>
            </div>

            {/* Estimated delivery */}
            <div className="delivery-estimate">
              📅 Estimated delivery: <strong>{getDeliveryDate('India')}</strong>
            </div>

            {/* Ingredients quick view */}
            {product.ingredients?.length > 0 && (
              <div className="ingredients-strip">
                <strong>Key Ingredients: </strong>
                {product.ingredients.slice(0, 5).join(', ')}
                {product.ingredients.length > 5 && '...'}
              </div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="product-tabs">
          <div className="tab-nav">
            {['description', 'ingredients', 'shipping', 'reviews'].map(tab => (
              <button
                key={tab}
                className={`tab-btn ${activeTab === tab ? 'active' : ''}`}
                onClick={() => setActiveTab(tab)}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>

          <div className="tab-content">
            {activeTab === 'description' && (
              <div className="tab-pane">
                <p>{product.description}</p>
              </div>
            )}

            {activeTab === 'ingredients' && (
              <div className="tab-pane">
                <h3>Ingredients</h3>
                <ul className="ingredients-list">
                  {product.ingredients?.map(ing => <li key={ing}>{ing}</li>)}
                </ul>
                {product.allergens?.length > 0 && (
                  <p className="allergen-note"><strong>Allergen Note:</strong> {product.allergens.join(', ')}</p>
                )}
              </div>
            )}

            {activeTab === 'shipping' && (
              <div className="tab-pane">
                <h3>Shipping Information</h3>
                <table className="shipping-table">
                  <thead>
                    <tr><th>Destination</th><th>Delivery Time</th></tr>
                  </thead>
                  <tbody>
                    <tr><td>🇮🇳 India</td><td>1–2 business days</td></tr>
                    <tr><td>🇺🇸 USA</td><td>3–7 business days</td></tr>
                    <tr><td>🇬🇧 UK</td><td>3–7 business days</td></tr>
                    <tr><td>🇸🇬 Singapore</td><td>3–7 business days</td></tr>
                    <tr><td>🇦🇺 Australia</td><td>3–7 business days</td></tr>
                    <tr><td>🇲🇾 Malaysia</td><td>3–7 business days</td></tr>
                  </tbody>
                </table>
              </div>
            )}

            {activeTab === 'reviews' && (
              <div className="tab-pane">
                {product.reviews?.length > 0 ? (
                  product.reviews.map(r => (
                    <div key={r._id} className="review-card">
                      <div className="review-header">
                        <span className="review-name">{r.name}</span>
                        <span className="review-stars">{'★'.repeat(r.rating)}</span>
                      </div>
                      <p className="review-text">{r.comment}</p>
                    </div>
                  ))
                ) : (
                  <p>No reviews yet. Be the first to review this product!</p>
                )}

                {/* Review submission form */}
                <div className="review-form">
                  {!user ? (
                    <p>Please <Link to="/login">login</Link> to write a review.</p>
                  ) : hasUserReviewed ? (
                    <p>You've already reviewed this product.</p>
                  ) : (
                    <>
                      <h3>Write a Review</h3>
                      <form onSubmit={handleReviewSubmit}>
                        <div className="star-selector">
                          {[1, 2, 3, 4, 5].map(star => (
                            <button
                              key={star}
                              type="button"
                              className={`star-btn ${star <= reviewRating ? 'active' : ''}`}
                              onClick={() => setReviewRating(star)}
                            >
                              ★
                            </button>
                          ))}
                        </div>
                        <textarea
                          className="review-textarea"
                          placeholder="Share your experience with this product..."
                          value={reviewComment}
                          onChange={e => setReviewComment(e.target.value)}
                          rows={4}
                        />
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={reviewSubmitting}
                          style={{ marginTop: '12px' }}
                        >
                          {reviewSubmitting ? 'Submitting...' : 'Submit Review'}
                        </button>
                      </form>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Related products */}
        {related.length > 0 && (
          <section className="related-section">
            <h2>Customers Also Bought</h2>
            <div className="related-grid">
              {related.map(p => <ProductCard key={p._id} product={p} />)}
            </div>
          </section>
        )}

        {/* Recently viewed */}
        {recentlyViewed.length > 0 && (
          <div className="recently-viewed">
            <h3>Recently Viewed</h3>
            <div className="rv-grid">
              {recentlyViewed.map(p => (
                <Link key={p._id} to={`/products/${p.slug}`} className="rv-card">
                  <img
                    src={p.thumbnail}
                    alt={p.name}
                    className="rv-img"
                  />
                  <p className="rv-name">{p.name}</p>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Sticky mobile buy bar — keeps the purchase CTA in view while scrolling */}
      <div className="sticky-buy-bar">
        <div className="sticky-buy-info">
          <span className="sticky-buy-name">{product.name}</span>
          <span className="sticky-buy-price">
            ₹{selectedVariant?.price}
            <span className="sticky-buy-weight"> / {selectedVariant?.weight}</span>
          </span>
        </div>
        {isOutOfStock ? (
          <button className="btn btn-outline sticky-buy-btn" disabled>Out of Stock</button>
        ) : (
          <>
            <button className="btn btn-outline sticky-buy-btn" onClick={handleAddToCart}>Add</button>
            <button className="btn btn-gold sticky-buy-btn" onClick={handleBuyNow}>Buy Now</button>
          </>
        )}
      </div>
    </div>
  );
};

export default ProductDetail;
